import { useCallback, RefObject } from 'react';
import { AI_FEEDBACK_SCORE } from '@tiendanube/common/src/common/AiFeedbackConstants';
import { AiFeature } from '@tiendanube/common/src/enums';
import { logError } from 'commons/logger';
import {
  TrackingAiFeatureProps,
  trackingAiFeedbackPositiveClick,
  trackingAiFeedbackNegativeClick,
} from 'domains/Ai/tracking';
import useModal from '../useModal';
import useProvideAiFeedback from '../useProvideAiFeedback';

interface UseAiFeedbackProps {
  feature: AiFeature;
  runIdRef?: RefObject<string | undefined>;
  onPositiveFeedback?: () => void;
  onNegativeFeedback?: () => void;
  trackingPositiveClick?: (
    trackingAiFeatureProps: TrackingAiFeatureProps,
  ) => void;
  trackingNegativeClick?: (
    trackingAiFeatureProps: TrackingAiFeatureProps,
  ) => void;
}

interface UseAiFeedbackResult {
  showFeedbackModal: boolean;
  handlePositiveFeedback: () => void;
  handleNegativeFeedback: () => void;
  closeFeedbackModal: () => void;
}

function useAiFeedback({
  feature,
  runIdRef,
  onPositiveFeedback,
  onNegativeFeedback,
  trackingPositiveClick,
  trackingNegativeClick,
}: UseAiFeedbackProps): UseAiFeedbackResult {
  const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] = useModal();
  const { sendAiGenerationFeedback } = useProvideAiFeedback();

  const handlePositiveFeedback = useCallback(() => {
    const currentRunId = runIdRef?.current;

    if (currentRunId) {
      sendAiGenerationFeedback({
        feedback: 'Positive feedback for AI generation',
        feature,
        run_id: currentRunId,
        score: AI_FEEDBACK_SCORE.POSITIVE,
      }).catch((error) => {
        logError('Error sending positive feedback', error as Error, {
          runId: currentRunId,
        });
      });
    }

    const trackingProps: TrackingAiFeatureProps = { feature };

    if (trackingPositiveClick) {
      trackingPositiveClick(trackingProps);
    } else {
      trackingAiFeedbackPositiveClick(trackingProps);
    }

    if (onPositiveFeedback) {
      onPositiveFeedback();
    }
  }, [
    runIdRef,
    feature,
    onPositiveFeedback,
    trackingPositiveClick,
    sendAiGenerationFeedback,
  ]);

  const handleNegativeFeedback = useCallback(() => {
    const trackingProps: TrackingAiFeatureProps = { feature };

    if (trackingNegativeClick) {
      trackingNegativeClick(trackingProps);
    } else {
      trackingAiFeedbackNegativeClick(trackingProps);
    }

    openFeedbackModal();

    if (onNegativeFeedback) {
      onNegativeFeedback();
    }
  }, [feature, onNegativeFeedback, trackingNegativeClick, openFeedbackModal]);

  return {
    showFeedbackModal,
    handlePositiveFeedback,
    handleNegativeFeedback,
    closeFeedbackModal,
  };
}

export default useAiFeedback;
